/**
 * Lógica pura de la biblioteca y de la firma (ADR-14): lo que la pantalla
 * DERIVA de lo que sirve la API — el recuento por estado de un cuaderno, a
 * qué ficha lleva «Abrir», en qué estado está la firma de criterio, las
 * fechas humanas, las tarjetas de «Mi revisión». Sin React y sin red, para
 * probarla sola.
 */
import type { Biblioteca, CuadernoFichas, CuadernoResumen, Ficha, FichaResumen, Firma, ItemStatus } from "@skein/contracts";

// ── Cuadernos ─────────────────────────────────────────────────────────

/** Fichas «de la casa» en git: con el criterio volcado o más allá. */
export const deLaCasa = (c: CuadernoResumen): number => c.estados.house + c.estados.reviewed + c.estados.published;
/** Fichas que esperan a una persona (bloqueadas o en borrador): se leen, no se firman. */
export const esperanPersona = (c: CuadernoResumen): number => c.estados.blocked + c.estados.draft;

/** La sublínea de «Fichas»: «8 por IA · 1 de la casa · 1 espera a una persona». */
export function porEstado(c: CuadernoResumen): string {
  const ia = c.estados.reviewedAi;
  const casa = deLaCasa(c);
  const per = esperanPersona(c);
  return [ia ? `${ia} por IA` : "", casa ? `${casa} de la casa` : "", per ? `${per} ${per === 1 ? "espera" : "esperan"} a una persona` : ""].filter(Boolean).join(" · ");
}

/** El título corto de un área («C · Clientes y mercado»): la API ya lo trae compuesto. */
export const etiquetaArea = (c: Pick<CuadernoResumen, "area" | "areaName">): string => (c.areaName.startsWith(`${c.area} ·`) ? c.areaName : `${c.area} · ${c.areaName}`);

export interface Area { code: string; label: string; cuadernos: CuadernoResumen[] }
export function areas(cs: readonly CuadernoResumen[]): Area[] {
  const m = new Map<string, Area>();
  for (const c of cs) {
    const a = m.get(c.area) ?? { code: c.area, label: etiquetaArea(c), cuadernos: [] };
    a.cuadernos.push(c);
    m.set(c.area, a);
  }
  return [...m.values()].sort((a, b) => a.code.localeCompare(b.code));
}

/**
 * «Abrir» un cuaderno lleva a su primera ficha con una pregunta pendiente
 * (en `reviewed-ai`, con preguntas y sin criterio vigente); si no queda
 * ninguna, a la primera que se pueda firmar, y si no, a la primera.
 */
export function primeraFicha(fichas: readonly FichaResumen[]): FichaResumen | undefined {
  // un criterio ya VOLCADO con la ficha aún en reviewed-ai y con preguntas es un «no lo sé» en git: vuelve a pedirse
  const pide = (f: FichaResumen) => !f.firmaVigente.criterio || f.firmaVigente.criterio.volcado === "volcada";
  return fichas.find((f) => f.status === "reviewed-ai" && f.preguntas > 0 && pide(f) && f.bloqueos.length === 0)
    ?? fichas.find((f) => f.status === "reviewed-ai")
    ?? fichas[0];
}
/** La primera ficha que espera una revisión (`house` sin revisión vigente). */
export function primeraRevision(fichas: readonly FichaResumen[]): FichaResumen | undefined {
  return fichas.find((f) => f.status === "house" && !f.firmaVigente.revision && f.bloqueos.length === 0) ?? fichas.find((f) => f.status === "house");
}

export type Orden = { k: "code" | "pend" | "firm"; dir: 1 | -1 };
export type FiltroEstado = "" | "preguntas" | "mias" | "persona";

/** Filtro y orden de la tabla (biblioteca.html): orden estable por código a igualdad. */
export function filtrar(cs: readonly CuadernoResumen[], o: { q: string; area: string; estado: FiltroEstado; soloMios: boolean; mios: readonly string[]; orden: Orden }): CuadernoResumen[] {
  const q = o.q.toLowerCase().trim();
  const mio = (c: CuadernoResumen) => o.mios.includes(c.code);
  const xs = cs.filter((c) =>
    (!q || `${c.code} ${c.title}`.toLowerCase().includes(q)) &&
    (!o.area || c.area === o.area) &&
    (!o.soloMios || mio(c)) &&
    (!o.estado || (o.estado === "preguntas" && c.preguntasPendientes > 0) || (o.estado === "mias" && mio(c) && c.firmadas > 0) || (o.estado === "persona" && esperanPersona(c) > 0)));
  const v = (c: CuadernoResumen): string | number => (o.orden.k === "code" ? c.code : o.orden.k === "pend" ? c.preguntasPendientes : c.fichas ? c.firmadas / c.fichas : 0);
  return xs.sort((a, b) => {
    const va = v(a), vb = v(b);
    return ((va < vb ? -1 : va > vb ? 1 : 0) * o.orden.dir) || a.code.localeCompare(b.code);
  });
}

// ── Fechas ────────────────────────────────────────────────────────────

const MES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const hm = (d: Date) => `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
const medianoche = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
const diasNaturales = (d: Date, ahora: Date) => Math.round((medianoche(ahora) - medianoche(d)) / 86_400_000);

/** «Última firma» de la tabla: «hace 2 h», «hoy 10:42», «ayer», «hace 3 días», «26 jul». */
export function haceCuanto(iso: string | null, ahora = new Date()): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  const min = Math.floor((ahora.getTime() - d.getTime()) / 60_000);
  const dias = diasNaturales(d, ahora);
  if (dias <= 0) return min < 1 ? "ahora" : min < 60 ? `hace ${min} min` : min < 6 * 60 ? `hace ${Math.floor(min / 60)} h` : `hoy ${hm(d)}`;
  if (dias === 1) return "ayer";
  if (dias < 7) return `hace ${dias} días`;
  return `${d.getDate()} ${MES[d.getMonth()]}`;
}
/** Fecha con hora de una firma: «hoy 10:42», «ayer 10:42», «19 oct 18:52». */
export function cuando(iso: string, ahora = new Date()): string {
  const d = new Date(iso);
  const dias = diasNaturales(d, ahora);
  if (dias === 0) return `hoy ${hm(d)}`;
  if (dias === 1) return `ayer ${hm(d)}`;
  return `${d.getDate()} ${MES[d.getMonth()]} ${hm(d)}`;
}
/** «31 oct 2027» (la caducidad de la ficha, AAAA-MM-DD). */
export function fechaDia(ymd: string): string {
  const [y, m, d] = ymd.split("-").map(Number);
  if (!y || !m || !d) return ymd;
  return `${d} ${MES[m - 1]} ${y}`;
}

// ── La ficha y su firma ───────────────────────────────────────────────

/** El rótulo humano del estado de git (el slug va en el globo). */
export const ESTADO_GIT: Record<ItemStatus, { label: string; tone: "accent" | "ok" | "warn" | undefined }> = {
  "reviewed-ai": { label: "revisada por IA", tone: "accent" },
  house: { label: "de la casa", tone: "ok" },
  reviewed: { label: "revisada por un socio", tone: "ok" },
  published: { label: "publicada", tone: "ok" },
  retired: { label: "retirada", tone: undefined },
  blocked: { label: "espera a una persona", tone: "warn" },
  draft: { label: "borrador: espera a una persona", tone: "warn" },
};
export const CLASE: Record<string, { label: string; tip: string }> = {
  definition: { label: "definición", tip: "Clase de la ficha: definición, un término de la casa" },
  indicator: { label: "indicador", tip: "Clase de la ficha: indicador, un dato con su lectura" },
  pattern: { label: "patrón", tip: "Clase de la ficha: patrón, una situación que se repite" },
  fact: { label: "hecho", tip: "Clase de la ficha: hecho, una norma o un dato con fecha" },
  sector: { label: "sector", tip: "Clase de la ficha: sector, cómo funciona uno" },
};

/** «no lo sé», «No lo sé.», «no lo se» → sí (la regla de la API y del volcado). */
export function esNoLoSe(s: string): boolean {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[\s.!¡¿?«»"]+/g, " ").trim() === "no lo se";
}
export const hash8 = (h: string): string => h.slice(0, 8);
const clases = { criterio: "criterio", revision: "revisión", despacho: "despacho" } as const;
export const claseFirma = (f: Pick<Firma, "kind">): string => clases[f.kind];
/** La decisión, como la dice el socio: «vale», «así no», «no lo sé». */
export function decisionFirma(f: Pick<Firma, "decision" | "answers">): string {
  if (f.decision === "asi-no") return "así no";
  return f.answers.length > 0 && f.answers.every(esNoLoSe) ? "no lo sé" : "vale";
}

export type EstadoCriterio =
  | { k: "bloqueada" }
  | { k: "asino"; firma: Firma; mia: boolean }
  | { k: "nolose"; firma: Firma; mia: boolean }
  | { k: "firmada"; firma: Firma; mia: boolean }
  | { k: "volcada"; firma: Firma; mia: boolean }
  | { k: "en-git" }
  | { k: "caducada"; firma: Firma }
  | { k: "no-dueno"; porque: string; sinDueno: boolean }
  | { k: "sin-firmar" };

const porFecha = (a: Firma, b: Firma) => a.createdAt.localeCompare(b.createdAt);

/**
 * En qué estado está la firma de CRITERIO de la ficha, para la card «Tu
 * criterio» (ficha.html, `?firma=`). Las firmas las calcula la API
 * (vigente/caducada/volcada); aquí solo se elige qué enseñar:
 * 1. un «así no» abierto (sin resolver, del texto actual) manda: la ficha
 *    espera al juez y nadie firma encima (INV-PS-4);
 * 2. un criterio «vale» vigente: «no lo sé» si todas sus respuestas lo son,
 *    «volcada» si git ya lo lleva, «firmada» si aún no;
 * 3. una ficha ya fuera de `reviewed-ai` sin firma en el Studio: su criterio
 *    está en git (lo firmó alguien antes de esta pantalla);
 * 4. mi criterio caducado (el texto cambió): se pide otra vez, con la
 *    respuesta de entonces;
 * 5. no puedo firmar: el porqué de la API;
 * 6. si no, el formulario.
 */
export function estadoCriterio(f: Ficha, meOid: string): EstadoCriterio {
  if (f.status === "blocked" || f.status === "draft") return { k: "bloqueada" };
  const crit = f.firmas.filter((x) => x.kind === "criterio").sort(porFecha);
  const asino = [...crit].reverse().find((x) => x.decision === "asi-no" && x.estado !== "caducada" && !x.resolucion);
  if (asino) return { k: "asino", firma: asino, mia: asino.signer.oid === meOid };
  const vig = crit.find((x) => x.estado === "vigente" && x.decision === "vale");
  // un «no lo sé» ya volcado: git lleva el cuerpo con el marcador y la ficha sigue en reviewed-ai.
  // No es un callejón sin salida: la pregunta se vuelve a pedir, en blanco (ADR-14 pt 5)
  if (vig && vig.volcado === "volcada" && f.status === "reviewed-ai" && f.preguntas.length > 0) {
    return f.puedoFirmar.criterio ? { k: "sin-firmar" } : { k: "no-dueno", porque: porqueHumano(f.puedoFirmar.porque.criterio ?? "No puedes firmar el criterio de esta ficha."), sinDueno: f.dueno === null };
  }
  if (vig) {
    const mia = vig.signer.oid === meOid;
    if (f.status === "reviewed-ai" && vig.answers.length > 0 && vig.answers.every(esNoLoSe) && vig.volcado !== "volcada") return { k: "nolose", firma: vig, mia };
    return vig.volcado === "volcada" ? { k: "volcada", firma: vig, mia } : { k: "firmada", firma: vig, mia };
  }
  if (f.status !== "reviewed-ai") return { k: "en-git" };
  const miaCaducada = [...crit].reverse().find((x) => x.signer.oid === meOid && x.decision === "vale" && x.estado === "caducada");
  if (miaCaducada && f.puedoFirmar.criterio) return { k: "caducada", firma: miaCaducada };
  if (!f.puedoFirmar.criterio) return { k: "no-dueno", porque: porqueHumano(f.puedoFirmar.porque.criterio ?? "No puedes firmar el criterio de esta ficha."), sinDueno: f.dueno === null };
  return { k: "sin-firmar" };
}

/**
 * Los párrafos «**Criterio de la casa (…):** …» de un cuerpo (vista previa, o
 * la ficha ya en git). Cada bloque es un párrafo entero: la composición de la
 * API colapsa los espacios de la respuesta, así que una respuesta no puede
 * partir su bloque con una línea en blanco.
 */
export function parrafosCriterio(body: string): string[] {
  return body.split(/\n\s*\n/).map((p) => p.trim()).filter((p) => p.includes("Criterio de la casa ("));
}

/** El porqué de un 403 de la biblioteca, en la voz de la pantalla (si la API no lo trae). */
const MOTIVO_403: Record<string, string> = {
  "no-owner": "El cuaderno no tiene dueño: quien lo tome firma su criterio.",
  "not-owner": "Solo firma el criterio quien es dueño del cuaderno.",
  "wrong-status": "La ficha no está en el estado que pide esta firma.",
  curator: "Eres quien curó la ficha: la revisión la firma otro socio.",
  "criterio-signer": "Firmaste el criterio: la revisión la firma otro socio.",
  role: "Esto es del Skein Team.",
  "not-professional": "Esta ficha no pide el visto del despacho.",
  blocked: "Hay un «así no» sin resolver: lo cierra el Skein Team o un cambio del texto.",
  forbidden: "Tu cuenta no tiene el rol de biblioteca.",
};
export const motivo403 = (code: string | undefined, message: string | undefined): string =>
  message && !/^\d{3} en /.test(message) ? porqueHumano(message) : (code && MOTIVO_403[code]) ?? "No tienes permiso para esto.";

// ── Mi revisión ───────────────────────────────────────────────────────

export type Tarjeta =
  | { k: "criterio"; cuaderno: CuadernoResumen }
  | { k: "revision"; cuaderno: CuadernoResumen; casa: number }
  | { k: "asino"; notebook: string; slug: string; title: string };

/**
 * Lo que le toca a quien mira (biblioteca.html, sidepane):
 * - las preguntas pendientes de MIS cuadernos (P1, ≈ dos minutos cada una);
 * - las fichas de la casa de cuadernos AJENOS, que puedo revisar como
 *   segundo socio (la API dice al abrir si de verdad puedo: no si curé la
 *   ficha o firmé su criterio);
 * - y, esperando a otros, las fichas con un «así no» abierto: las de mis
 *   cuadernos, o todas con el rol completo (las ve el Skein Team).
 */
export function tarjetas(b: Biblioteca, detalle: readonly CuadernoFichas[]): Tarjeta[] {
  const mios = new Set(b.me.misCuadernos);
  const out: Tarjeta[] = [];
  for (const c of b.cuadernos) if (mios.has(c.code) && c.preguntasPendientes > 0) out.push({ k: "criterio", cuaderno: c });
  for (const c of b.cuadernos) if (!mios.has(c.code) && c.estados.house > 0) out.push({ k: "revision", cuaderno: c, casa: c.estados.house });
  for (const d of detalle) for (const f of d.fichas) if (f.bloqueos.length > 0) out.push({ k: "asino", notebook: d.cuaderno.code, slug: f.slug, title: f.title });
  return out;
}

// ── La búsqueda de las rutas ──────────────────────────────────────────

/** `/conocimiento?q=C10&estado=vacio` y `/conocimiento/$slug?estado=error&firma=volcada`: solo cadenas no vacías. */
export interface BusquedaConocimiento { q?: string; estado?: string; firma?: string; uso?: string }
export function leerBusqueda(raw: Record<string, unknown>): BusquedaConocimiento {
  const out: BusquedaConocimiento = {};
  for (const k of ["q", "estado", "firma", "uso"] as const) {
    const v = raw[k];
    if (typeof v === "string" && v !== "") out[k] = v;
  }
  return out;
}

// ── Textos humanos ────────────────────────────────────────────────────

/** «hoy a las 10:42», «ayer a las 10:42», «el 19 oct a las 18:52»: para frases («Firmaste …»). */
export function elDia(iso: string, ahora = new Date()): string {
  const d = new Date(iso);
  const dias = diasNaturales(d, ahora);
  const h = `a las ${hm(d)}`;
  if (dias === 0) return `hoy ${h}`;
  if (dias === 1) return `ayer ${h}`;
  return `el ${d.getDate()} ${MES[d.getMonth()]} ${h}`;
}
/** Un slug a la vista, sin guiones (último recurso cuando no hay título). */
export const sinGuiones = (s: string): string => s.replace(/-/g, " ");
/** La licencia de una fuente, con su rótulo: «curado-de-fuente-reservada» → «curada de fuente reservada». */
const LICENCIA: Record<string, string> = { "curado-de-fuente-reservada": "curada de fuente reservada", "CC-BY": "CC BY", "CC-BY-SA": "CC BY-SA" };
export const licencia = (l: string): string => LICENCIA[l] ?? sinGuiones(l);

/**
 * Los porqués de la API llevan el estado de git en slug y el `curator` del
 * contrato; la pantalla los dice en su voz (sin cambiar el sentido).
 */
export function porqueHumano(msg: string): string {
  if (/curator/.test(msg)) return "Curaste esta ficha: la revisión la firma otro socio.";
  if (/professional_review/.test(msg)) return "Esta ficha no pide el visto del despacho.";
  return msg.replace(/\b(reviewed-ai|house|reviewed|published|retired|blocked|draft)\b/g, (s) => `«${ESTADO_GIT[s as ItemStatus].label}»`);
}

/** El veredicto de una ronda del método, en badge: «APTA» → apta (ok), «REVISAR» → revisar (warn)… */
export function veredicto(v: string): { label: string; tone: "ok" | "warn" | "danger" | undefined } {
  const k = v.trim().toLowerCase();
  if (k === "apta" || k === "apto" || k === "cierre") return { label: k, tone: "ok" };
  if (k === "revisar") return { label: k, tone: "warn" };
  if (k === "no apta" || k === "no apto" || k === "bloquear") return { label: k, tone: "danger" };
  return { label: k, tone: undefined };
}
